import React, { useState } from 'react';
import { Medication } from './types';
import { MedicationForm } from './MedicationForm';
import { getUnitForMedication } from './data/medications';

interface MedicationHistoryProps {
  entries: Medication[];
  onUpdate: (id: string, data: Medication) => void;
  onDelete: (id: string) => void;
  filter: 'all' | 'today' | 'week' | 'month' | 'quarter' | 'year';
}

export const MedicationHistory: React.FC<MedicationHistoryProps> = ({ 
  entries, 
  onUpdate, 
  onDelete,
  filter 
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleEdit = (entry: Medication) => {
    setEditingId(entry.id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = (data: Medication) => {
    if (editingId) {
      onUpdate(editingId, { ...data, id: editingId });
      setEditingId(null);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten wpis?')) {
      onDelete(id);
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (filter === 'all') return true;
    
    const entryDate = new Date(entry.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (filter === 'today') {
      return entryDate.getTime() === today.getTime();
    }
    
    if (filter === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return entryDate >= weekAgo;
    }
    
    if (filter === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return entryDate >= monthAgo;
    }
    
    if (filter === 'quarter') {
      const quarterAgo = new Date(today);
      quarterAgo.setMonth(quarterAgo.getMonth() - 3);
      return entryDate >= quarterAgo;
    }
    
    if (filter === 'year') {
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      return entryDate >= yearAgo;
    }
    
    return true;
  });

  if (editingId) {
    const entryToEdit = entries.find(e => e.id === editingId);
    if (entryToEdit) {
      return (
        <div>
          <h3 style={{ marginBottom: '20px' }}>Edytuj lek</h3>
          <MedicationForm
            initialData={entryToEdit}
            onSubmit={handleSaveEdit}
            onCancel={handleCancelEdit}
          />
        </div>
      );
    }
  }

  if (filteredEntries.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#666',
        backgroundColor: '#f9f9f9',
        borderRadius: '10px',
        marginTop: '20px'
      }}>
        <p style={{ fontSize: '18px', marginBottom: '10px' }}>Brak wpisów dla wybranego okresu</p>
        <p style={{ fontSize: '14px' }}>Dodaj pierwszy lek, aby rozpocząć śledzenie</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      {filteredEntries.map((entry) => (
        <div
          key={entry.id}
          style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '15px',
            backgroundColor: entry.taken ? '#e8f5e9' : '#fff3e0',
            borderLeft: `4px solid ${entry.taken ? '#4CAF50' : '#ff9800'}`
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, color: '#333' }}>{entry.name}</h3>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  backgroundColor: entry.taken ? '#4CAF50' : '#ff9800',
                  color: 'white'
                }}>
                  {entry.taken ? '✓ Przyjęty' : '○ Nie przyjęty'}
                </span>
              </div>
              <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
                <strong>Data i godzina:</strong> {entry.date} o {entry.time}
              </div>
              <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
                <strong>Liczba sztuk:</strong> {typeof entry.dosage === 'number' ? entry.dosage : parseInt(entry.dosage as any) || 1} {(() => {
                  const unit = getUnitForMedication(entry.name);
                  const dosageNum = typeof entry.dosage === 'number' ? entry.dosage : parseInt(entry.dosage as any) || 1;
                  if (unit === 'tabletka') {
                    return dosageNum === 1 ? 'tabletka' : dosageNum < 5 ? 'tabletki' : 'tabletek';
                  } else if (unit === 'kapsułka') {
                    return dosageNum === 1 ? 'kapsułka' : dosageNum < 5 ? 'kapsułki' : 'kapsułek';
                  } else if (unit === 'saszetka') {
                    return dosageNum === 1 ? 'saszetka' : dosageNum < 5 ? 'saszetki' : 'saszetek';
                  } else if (unit === 'czopek') {
                    return dosageNum === 1 ? 'czopek' : dosageNum < 5 ? 'czopki' : 'czopków';
                  } else if (unit === 'wlewka') {
                    return dosageNum === 1 ? 'wlewka' : dosageNum < 5 ? 'wlewki' : 'wlewek';
                  } else {
                    return dosageNum === 1 ? 'sztuka' : dosageNum < 5 ? 'sztuki' : 'sztuk';
                  }
                })()}
              </div>
              <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
                <strong>Częstotliwość:</strong> {entry.frequency}
              </div>
              {entry.withMeal && (
                <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
                  <strong>🍽️ W trakcie posiłku</strong>
                </div>
              )}
              {entry.notes && (
                <div style={{ color: '#666', fontSize: '14px', marginTop: '10px', fontStyle: 'italic' }}>
                  <strong>Notatki:</strong> {entry.notes}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
              <button
                onClick={() => handleEdit(entry)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Edytuj
              </button>
              <button
                onClick={() => handleDelete(entry.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
